import { useState, useEffect } from 'react'
import { useEditorStore } from '../store/editor.store'
import { exportToHTML } from '../utils/htmlExport'

// ── §72 Code Mode — generated HTML/CSS view for developers ────────────────────

type Tab = 'html' | 'preview'

export function CodeViewModal({ onClose }: { onClose: () => void }) {
  const { page, settings } = useEditorStore()
  const [tab, setTab] = useState<Tab>('html')
  const [copied, setCopied] = useState(false)
  const [html, setHtml] = useState('')

  useEffect(() => {
    setHtml(exportToHTML(page, settings))
  }, [page, settings])

  function handleCopy() {
    navigator.clipboard.writeText(html).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  function handleDownload() {
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(settings.name || 'site').toLowerCase().replace(/\s+/g, '-')}.html`
    a.click()
    URL.revokeObjectURL(url)
  }

  const lineCount = html.split('\n').length

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 9800, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ width: '100%', maxWidth: 820, background: '#1e1e2e', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 40px rgba(0,0,0,0.4)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #313244', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#181825' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: '#cdd6f4', fontFamily: 'monospace' }}>{'</>'}</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#cdd6f4' }}>Code View</div>
              <div style={{ fontSize: 11, color: '#6c7086' }}>§72 · {lineCount} строк · {(html.length / 1024).toFixed(1)} KB</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={handleCopy}
              style={{ padding: '6px 14px', background: copied ? '#a6e3a1' : '#313244', color: copied ? '#1e1e2e' : '#cdd6f4', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
            >
              {copied ? '✓ Скопировано' : '⎘ Копировать'}
            </button>
            <button
              onClick={handleDownload}
              style={{ padding: '6px 14px', background: '#313244', color: '#cdd6f4', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
            >
              ↓ Скачать
            </button>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', color: '#6c7086', cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '2px 6px' }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #313244', background: '#181825' }}>
          {(['html', 'preview'] as Tab[]).map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                padding: '8px 20px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, fontFamily: 'monospace',
                color: tab === t ? '#89b4fa' : '#6c7086',
                borderBottom: tab === t ? '2px solid #89b4fa' : '2px solid transparent',
                transition: 'all 0.15s',
              }}
            >
              {t === 'html' ? 'HTML' : 'Preview'}
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'html' ? (
          <pre style={{
            flex: 1, overflowY: 'auto', margin: 0, padding: '16px 20px',
            fontFamily: '"Fira Code", "Cascadia Code", "JetBrains Mono", monospace',
            fontSize: 12, lineHeight: 1.6, color: '#cdd6f4',
            background: '#1e1e2e', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>
            {html}
          </pre>
        ) : (
          <iframe
            srcDoc={html}
            style={{ flex: 1, border: 'none', background: '#fff' }}
            sandbox="allow-scripts allow-same-origin"
            title="Page preview"
          />
        )}
      </div>
    </div>
  )
}
